import React, { useState } from 'react';
import PropTypes from 'prop-types';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { Collapse } from '@mui/material';
import numeral from 'numeral';

import { useTheme } from 'contexts/ThemeContext';

import { Text } from 'components/DataDisplay';
import Image from 'components/Image';
import { Container, Item } from 'components/Layout';

import { AudienceGroup } from './AudienceGroup';
import { InlineIdEditor } from './InlineIdEditor';
import { StatusPill } from './MediaStatusPill';
import { PendingChangesBlock } from './PendingChangesBlock';

const countPendingModifications = pendingChange => {
  if (!pendingChange) {
    return 0;
  }
  const {
    budget,
    dates,
    geoTargeting,
    modifiedAds,
    newCreatives,
    toggledAds
  } = pendingChange;
  const adsCount = modifiedAds.reduce(
    (total, ad) => total + ad.fields.length,
    0
  );
  return (
    (budget ? 1 : 0) +
    (dates ? 1 : 0) +
    (geoTargeting ? 1 : 0) +
    adsCount +
    newCreatives.length +
    (toggledAds || []).length
  );
};

export const RecapMediaCard = ({ card }) => {
  const { themeColors } = useTheme();
  const { designSystem } = themeColors;
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    logo,
    mediaName,
    mediaKey,
    status,
    volume,
    total,
    audiences,
    pendingChange,
    externalId,
    onSaveId
  } = card;

  const pendingModifications = countPendingModifications(pendingChange);

  return (
    <Container
      flex
      style={{
        width: '100%',
        flexDirection: 'column',
        alignItems: 'stretch',
        border: `1px solid ${designSystem.line}`,
        borderRadius: '9px',
        overflow: 'hidden',
        marginBottom: '10px'
      }}
    >
      <Item
        flex
        onClick={() => setIsExpanded(prev => !prev)}
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          padding: '10px 12px',
          backgroundColor: designSystem.bgSoft,
          cursor: 'pointer',
          ...(isExpanded
            ? { borderBottom: `1px solid ${designSystem.line}` }
            : {})
        }}
      >
        <Item
          flex
          style={{
            alignItems: 'center',
            gap: '10px',
            justifyContent: 'flex-start'
          }}
        >
          <Image src={logo} alt={mediaKey} height="28px" />
          <Text component="span" fontSize="13px" fontWeight={700}>
            {mediaName}
          </Text>
          <StatusPill status={status} />
          {pendingModifications > 0 && (
            <Text
              component="span"
              fontSize="10px"
              fontWeight={700}
              style={{
                color: designSystem.violet,
                backgroundColor: designSystem.violetBg,
                borderRadius: '5px',
                padding: '2px 7px',
                whiteSpace: 'nowrap'
              }}
            >
              {pendingModifications} modif. en attente
            </Text>
          )}
          <InlineIdEditor value={externalId} onSave={onSaveId} />
        </Item>
        <Item
          flex
          style={{
            alignItems: 'center',
            gap: '12px',
            justifyContent: 'flex-end'
          }}
        >
          {volume !== 0 && (
            <Text
              component="span"
              fontSize="11.5px"
              style={{ color: designSystem.muted, whiteSpace: 'nowrap' }}
            >
              ≈ {numeral(volume).format('0,0')} impressions
            </Text>
          )}
          <Text
            component="span"
            fontSize="13px"
            fontWeight={700}
            style={{ color: designSystem.ink }}
          >
            {numeral(total).format('0,0 $')}
          </Text>
          {isExpanded ? (
            <KeyboardArrowDownIcon style={{ fill: designSystem.faint }} />
          ) : (
            <KeyboardArrowRight style={{ fill: designSystem.faint }} />
          )}
        </Item>
      </Item>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Item
          flex
          style={{
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: '8px',
            padding: '8px'
          }}
        >
          {pendingChange && (
            <PendingChangesBlock pendingChange={pendingChange} />
          )}
          {audiences.length > 0 ? (
            audiences.map(audience => (
              <AudienceGroup audience={audience} key={audience.id} />
            ))
          ) : (
            <Text
              component="span"
              fontSize="12.5px"
              style={{ color: designSystem.muted, paddingLeft: '6px' }}
            >
              Aucune audience rattachée.
            </Text>
          )}
        </Item>
      </Collapse>
    </Container>
  );
};

RecapMediaCard.propTypes = {
  card: PropTypes.shape().isRequired
};

export default RecapMediaCard;
